<template>
  <div class="media-library">
    <header class="media-library__header">
      <transition name="fade-in">
        <FilterToggleBtn @toggleMobileFilters="toggleMobileFilters" />
      </transition>
    </header>
    <Loader v-if="isLoading" />
    <transition name="fade-in">
      <div v-if="!isLoading">
        <div class="media-library__cols" key="cols">
          <div class="media-library__col-1">
            <transition name="toggle-filters">
              <FiltersMobile
                  :filters="cleanedFilters"
                  :settings="settings"
                  :activeFilters="activeFilters"
                  :mobileFiltersOpen="mobileFiltersOpen"
                  @filterChanged="changeFilter"
                  @closeFilters="toggleMobileFilters"
                  @clearFilters="clearFilters"
              />
            </transition>
            <transition name="fade-in">
              <FiltersDesktop
                  :filters="cleanedFilters"
                  :resetLabel="settings.filtersReset"
                  :activeFilters="activeFilters"
                  @filterChanged="changeFilter"
                  @clearFilters="clearFilters"
              />
            </transition>
          </div>
          <div class="media-library__col-2">
            <Search
                :settings="settings"
                @searchStringChange="search"
            />
            <Items
                :downloads="filteredDownloads"
                :isEmpty="filteredDownloads.length === 0"

                :isLazyLoading="isLazyLoading"
                :isFiltering="isFiltering"
                :settings="settings"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
<script setup>
import { ref, computed } from 'vue';
import Loader from './components/Loader/Loader.vue';
import Search from './components/Search/Search.vue';
import FilterToggleBtn from './components/Filters/FilterToggleBtn.vue';
import FiltersMobile from './components/Filters/FiltersMobile.vue';
import FiltersDesktop from './components/Filters/FiltersDesktop.vue';
import Items from './components/Items/Items.vue';

const isLoading = ref( false );
const filters = ref( [] );
const downloads = ref( [] );
const settings = ref( null );
const mobileFiltersOpen = ref( false );
const searchString = ref( '' );
const downloadsPerPage = ref( 9 );
const pageNr = ref( 1 );
const pageFooter = ref( document.getElementById( 'site-footer' ) );
const isLazyLoading = ref( false );
const lazyLoadingDone = ref( false );
const isFiltering = ref( false );
const selectedFilters = ref( null );

const cleanedFilters = computed( () => {
  if ( ! filters.value ) {
    return [];
  }

  return filters.value.map( group => ( {
    ...group,
    items: group.items.filter( item => item.isVisible )
  } ) ).filter( group => group.items.length > 0 );
} );

/**
 * Get all active filters.
 *
 * @return {Array}
 */
const activeFilters = computed( () => {
  if ( ! filters.value ) {
    return [];
  }

  return filters.value.flatMap( block => block.items.filter( item => item.isActive ) );
} );

const activeDownloads = computed( () => {
  if ( ! downloads.value ) {
    return [];
  }

  const activeDownloads = downloads.value.filter( download => download.isActive );

  if ( searchString.value.length > 0 ) {
    const searchStringLower = searchString.value.toLowerCase();
    activeDownloads.forEach( download => {
      download.isFilteredOut = ! download.title.toLowerCase().includes( searchStringLower );
    } );
  } else {
    activeDownloads.forEach( download => {
      download.isFilteredOut = false;
    } );
  }

  return activeDownloads;
} );

const filteredDownloads = computed( () => {
  let filteredDownloads = [];

  const nrOfDownloadsToBeFiltered = activeDownloads.value.filter( download => ! download.isFilteredOut )
      .length;

  activeDownloads.value.map( download => {
    if ( filteredDownloads.length < downloadsPerPage.value * pageNr.value && ! download.isFilteredOut ) {
      filteredDownloads.push( download );
    }
  } );

  lazyLoadingDone.value = nrOfDownloadsToBeFiltered < downloadsPerPage.value * pageNr.value;

  return filteredDownloads;
} );

const isPreselected = ( id ) => {
  const flatArray = Object.values( selectedFilters.value ).flat();
  return flatArray.includes( id );
};

const toggleMobileFilters = () => mobileFiltersOpen.value = ! mobileFiltersOpen.value;

const lazyLoadDownloads = ( el ) => {
  const rect = el.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;
  const vertInView = rect.top <= windowHeight && rect.top + rect.height >= 0;
  const horInView = rect.left <= windowWidth && rect.left + rect.width >= 0;

  if ( vertInView && horInView ) {
    // Footer IS in viewport, so try to lazyload downloads;
    if ( ! lazyLoadingDone.value ) {
      isLazyLoading.value = true;
      setTimeout( () => {
        pageNr.value++;
        isLazyLoading.value = false;
      }, 800 );
    }
  }
};

const getActiveFilters = () => {
  const mediaLibrary = document.querySelector( '.td-media-library' );

  if ( ! mediaLibrary ) {
    selectedFilters.value = null;
    return;
  }

  selectedFilters.value = JSON.parse( mediaLibrary.dataset.selectedFilters );
};

const changeFilter = ( filter ) => {
  isFiltering.value = true;
  pageNr.value = 1;
  const activeFiltersArray = [];

  // First set active state all filters
  filters.value.map( block => {
    block.items.map( item => {

      if ( filter === item ) {
        item.isActive = ! item.isActive;
      }

      // Check if the filter ID matches the active vendor from the CMS
      else if ( filter === null && isPreselected( item.id ) ) {
        item.isActive = true;
      }

      // fill active filters array so we don't have to loop over all filters again
      // in the code block below where we set the active downloads
      if ( item.isActive ) {
        activeFiltersArray.push( item.id );
      }
    } );
  } );

  downloads.value.map( download => {
    download.isActive = false;
    if ( activeFiltersArray.length === 0 ) {
      // Show all downloads
      download.isActive = true;
    } else {
      // Show downloads that match all active filters
      if ( activeFiltersArray.every( id => download.terms.includes( id ) ) ) {
        download.isActive = true;
      }
    }
  } );

  let possibleTerms = [];
  activeDownloads.value.map( download => {
    download.terms.forEach( term => {
      if ( ! possibleTerms.includes( term ) ) {
        possibleTerms.push( term );
      }
    } );
  } );

  filters.value.map( block => block.items.map( filter => filter.isVisible = possibleTerms.includes( filter.id ) ) );

  setTimeout( () => {
    isFiltering.value = false;
    lazyLoadDownloads( pageFooter.value );
  }, 800 );
};

const clearFilters = () => {
  isFiltering.value = true;
  pageNr.value = 1;
  filters.value.map( block => {
    block.items.map( filter => {
      filter.isActive = false;
      filter.isVisible = true;
    } );
  } );

  downloads.value.map( download => download.isActive = true );

  setTimeout( () => {
    isFiltering.value = false;
  }, 300 );
};

const search = ( value ) => {
  pageNr.value = 1;
  searchString.value = value;
};

// Helper function to make the scroll eventlistener less expensive
function debounce( func, wait = 50, immediate = true ) {
  var timeout;
  return function () {
    const context = this,
        args = arguments;
    const later = function () {
      timeout = null;
      if ( ! immediate ) func.apply( context, args );
    };
    const callNow = immediate && ! timeout;
    clearTimeout( timeout );
    timeout = setTimeout( later, wait );
    if ( callNow ) func.apply( context, args );
  };
}

// created() — runs at setup time (no DOM access, safe at top level)
isLoading.value = true;

fetch( `${ window.wp_variables.rest_api_url }connect/v1/media-library` )
    .then( response => {
      if ( ! response.ok ) {
        throw new Error( 'Network response was not ok' );
      }
      return response.json();
    } )
    .then( data => {
      isLoading.value = false;
      filters.value = data.filters;
      downloads.value = data.posts;
      settings.value = data.settings;

      getActiveFilters();
      changeFilter( null );

      // Try to lazyload on page load
      setTimeout( () => lazyLoadDownloads( pageFooter.value ), 500 );
    } )
    .catch( error => console.error( 'There was a problem with the fetch operation:', error ) );

window.addEventListener( 'scroll', debounce(
    () => lazyLoadDownloads( pageFooter.value ),
    50,
    false
) );
</script>
<style>
.media-library {
  display: grid;
}
</style>
